import * as React from "react";
import { cn } from "../lib/cn";

export interface StudioPillProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Optional leading icon (Lucide component). */
  icon?: React.ReactNode;
  /** Whether the corresponding StudioDrawer is open — controls visual active state. */
  open?: boolean;
}

/**
 * StudioPill — brand top-right pill that toggles the Ando Studio drawer.
 *
 * Sits in the header (next to the user menu). Visually the only place in
 * the chrome that uses both Ando colours (orange gradient → violet) — it's
 * the entry point to the design / model / template settings.
 */
export const StudioPill = React.forwardRef<HTMLButtonElement, StudioPillProps>(
  ({ icon, open, className, children, type, ...rest }, ref) => (
    <button
      ref={ref}
      type={type ?? "button"}
      data-state={open ? "open" : "closed"}
      className={cn(
        "inline-flex h-8 items-center gap-1.5 rounded-full px-3 font-medium text-white text-xs",
        "bg-[linear-gradient(135deg,#FF7A1A_0%,#E71067_55%,#9B5DE5_100%)] shadow-sm",
        "hover:-translate-y-px transition-transform duration-fast ease-smooth focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        open && "ring-2 ring-ring ring-offset-2",
        className
      )}
      {...rest}
    >
      {icon ? <span className="shrink-0">{icon}</span> : null}
      <span>{children ?? "Studio"}</span>
    </button>
  )
);
StudioPill.displayName = "StudioPill";
